import { ReactNode, useId } from "react";
import { buildClasses } from "../utils/buildClasses";
import { RYE_CARD_SURFACE } from "./RyeCard";
import { RyeIcon } from "./RyeIcon";

/**
 * What a screen shows before it has anything to show: what the screen is for
 * and the action that gets started. Fills its container and sits slightly
 * above center, which reads as centered. Compact is for smaller surfaces, such
 * as a pane in the Settings modal.
 */
export function RyeEmptyState({
  icon,
  title,
  description,
  note,
  actions,
  size = "default",
  wide = false,
  children,
}: {
  icon: string;
  title: string;
  /** What this screen is for, in a sentence or two. */
  description: ReactNode;
  /** A smaller follow-up line, e.g. something to do first. */
  note?: ReactNode;
  /** Buttons that get started, the primary one first. */
  actions?: ReactNode;
  size?: "default" | "compact";
  /** Room for more, such as a preview in `children`. */
  wide?: boolean;
  /** Anything more, below the actions, such as a preview of what they make. */
  children?: ReactNode;
}): JSX.Element {
  const titleId = useId();
  const compact = size === "compact";

  return (
    <div
      className={buildClasses(
        "h-full",
        "flex",
        "flex-col",
        "items-center",
        "px-4",
        "py-8",
      )}
    >
      {/* Unequal spacers put the card at ~40% of the height (optical center). */}
      <div className={buildClasses("flex-[2]")} />
      <section
        aria-labelledby={titleId}
        className={buildClasses(
          "w-full",
          "flex",
          "flex-col",
          "items-center",
          RYE_CARD_SURFACE,
          "text-center",
          {
            if: compact,
            then: buildClasses("gap-3", "px-6", "py-8"),
            else: buildClasses("gap-4", "px-8", "py-10"),
          },
          {
            if: wide,
            then: "max-w-2xl",
            else: compact ? "max-w-md" : "max-w-lg",
          },
        )}
      >
        <div
          aria-hidden="true"
          className={buildClasses(
            "flex",
            "items-center",
            "justify-center",
            "rounded-full",
            "bg-purple-100",
            "text-purple",
            "ring-purple-50",
            {
              if: compact,
              then: buildClasses("w-11", "h-11", "ring-[6px]"),
              else: buildClasses("w-14", "h-14", "ring-8"),
            },
          )}
        >
          <RyeIcon name={icon} size={compact ? "md" : "lg"} />
        </div>
        <h2
          id={titleId}
          className={buildClasses("font-medium", "text-on-surface-800", {
            if: compact,
            then: buildClasses("mt-1", "text-lg"),
            else: buildClasses("mt-2", "text-xl"),
          })}
        >
          {title}
        </h2>
        <p
          className={buildClasses(
            "max-w-md",
            "text-on-surface-600",
            "text-pretty",
            { if: compact, then: "text-sm" },
          )}
        >
          {description}
        </p>
        {note && (
          <p
            className={buildClasses(
              "max-w-md",
              "text-sm",
              "text-on-surface-500",
              "text-pretty",
            )}
          >
            {note}
          </p>
        )}
        {actions && (
          <div
            className={buildClasses(
              "flex",
              "flex-wrap",
              "items-center",
              "justify-center",
              "gap-3",
              "mt-2",
            )}
          >
            {actions}
          </div>
        )}
        {children}
      </section>
      <div className={buildClasses("flex-[3]")} />
    </div>
  );
}
